import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { Place as PlaceIcon } from '@mui/icons-material';

import { PLACES_AUTOCOMPLETE_LIMITS } from '@bandido/shared/types';

import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { connectorCapabilitiesAreActionable } from '@frontend/features/rutero/connectorCapabilitiesQueryKey';
import { useAutocompleteFocus } from '../useAutocompleteFocus';
import {
  apiErrorMessage,
  ConnectorInfo,
  useConnectorCapabilities,
  useGeocodeAutocomplete,
  useGeocodeResolve,
  usePlacesAutocomplete,
} from '../queries';
import { deriveServedByForVertical } from '../connectorCapabilityResolution';
import { defaultAttemptIdFactory } from '../holdAttemptIdentity';
import {
  PlaceRef,
  flattenPlaceOptions,
  placesNoticeReason,
  sortPlaceOptionsForDisplay,
  toPlaceRef,
} from '../places';
import {
  GEOCODE_QUERY_MIN_LENGTH,
  GeocodePointDraft,
  geocodeNoticeReason,
  geocodeResolveErrorOffersManualFallback,
  geocodeShouldOfferManualFallback,
  splitGeocodeDescription,
} from '../places/geocode';
import {
  TRANSFER_POINT_PLACE_KINDS,
  TRANSFER_POINT_RESOLVE_IDLE,
  TransferPointAddressOption,
  TransferPointOption,
  TransferPointResolveEvent,
  TransferPointResolveState,
  TransferPointValue,
  buildTransferPointOptions,
  nextGeocodeSessionToken,
  transferPointNotice,
  transferPointPlacesAuthority,
  transferPointResolveSettled,
  transferPointResolveStateAfter,
  transferPointNoticeIsPersistent,
  transferPointOptionGroupLabelKey,
  transferPointOptionLabel,
  transferPointSelectedOption,
} from '../transferPointPicker';
import { PlaceAutocompletePopper } from './PlaceAutocomplete';
import { PlaceOptionRow } from './PlaceOptionRow';

/** Lo que el asesor eligió: un lugar del maestro, o una dirección ya resuelta. */
export type TransferPointSelection =
  | { source: 'place'; place: PlaceRef }
  | { source: 'address'; point: GeocodePointDraft };

interface Props {
  label: string;
  placeholder: string;
  value: TransferPointValue | null;
  onChange: (selection: TransferPointSelection | null) => void;
  /**
   * Avisa al contenedor que hay un resolve EN VUELO. Lo necesita para
   * bloquear el swap de origen/destino: este componente vive en una posición
   * FIJA y su `onChange` apunta siempre al mismo extremo, así que invertir a
   * mitad de una resolución escribiría las coordenadas en el punto
   * equivocado.
   */
  onResolvingChange?: (resolving: boolean) => void;
  onUseManualFallback: () => void;
  /**
   * Cambia cuando el formulario reemplaza el punto DESDE AFUERA del campo:
   * "Limpiar" la búsqueda, o invertir origen y destino. No alcanza con mirar
   * `value`: el punto borrado puede haber estado vacío desde el principio
   * —justo el caso de la PRIMERA dirección elegida— y ahí el valor no cambia,
   * pero el intento en curso igual tiene que quedar tardío. Aplicar una
   * búsqueda reciente no necesita esto: remonta el buscador entero
   * (`useSearchFormKey`).
   */
  resetKey: number;
  width?: number | string;
}

/**
 * El campo ÚNICO de un punto de traslado (#2227): un solo desplegable
 * clasificado que ofrece a la vez los lugares del maestro (aeropuertos,
 * estaciones y puertos, agrupados y con su chip de tipo y su código) y las
 * direcciones y hoteles del resolver de Google. El asesor no elige antes
 * "qué tipo de punto" está por cargar — busca, y el TIPO es una propiedad de
 * lo que eligió. Origen y destino son el MISMO control: aeropuerto → hotel y
 * hotel → aeropuerto se cargan sin tocar ningún selector.
 *
 * Las dos fuentes son mecanismos distintos y fallan por separado (maestro:
 * `plc_*`, cacheado, filtrado por `servedBy`; resolver: sesión de Google con
 * cupo diario y costo por resolución, `docs/PLACES.md` §9), así que el
 * estado del campo NO es un booleano: `transferPointNotice` decide cuál de
 * las dos contesta y lo dice, porque una lista a medias se ve igual de sana
 * que una completa. Con las dos caídas queda la carga manual, que nunca
 * desaparece.
 *
 * Toda la lógica pura vive en `../transferPointPicker.ts`: este archivo es
 * `.tsx` y el jest del repo no resuelve `.tsx` a propósito (ver
 * `jest.config.cjs`).
 */
export function TransferPointAutocomplete({
  label,
  placeholder,
  value,
  onChange,
  onResolvingChange,
  onUseManualFallback,
  resetKey,
  width = 280,
}: Props) {
  const { t } = useTranslation();
  const [debouncedQuery, setQuery] = useDebouncedState('', 250);
  /**
   * Un UUID por sesión de TIPEO (mismo helper que `holdAttemptIdentity.ts`),
   * generado al enfocar y descartado al elegir o al salir del campo — sin
   * esto Google factura cada pulsación por separado. Mientras es `null` la
   * query de direcciones queda deshabilitada.
   */
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  /**
   * El intento de resolver una dirección, como UN estado (no dos booleanos):
   * un `resolve` que devuelve OTRO lugar es un fallo aunque HTTP diga 200, y
   * el fallo tiene que morir cuando el asesor elige otra cosa.
   */
  const [resolveState, setResolveState] = useState<TransferPointResolveState>(
    TRANSFER_POINT_RESOLVE_IDLE,
  );
  const resolving = resolveState.status === 'resolving';
  const resolveFailed = resolveState.status === 'failed';
  // Por ref y no por closure: los callbacks de la mutación corren fuera del
  // render que los creó, y la transición tiene que partir del estado vigente.
  // No se hace dentro del updater de `setState` a propósito: ahí adentro no
  // pueden vivir efectos (React puede reejecutarlo), y comitear el punto ES
  // un efecto.
  const resolveStateRef = useRef(resolveState);
  resolveStateRef.current = resolveState;
  const applyResolveEvent = (event: TransferPointResolveEvent) => {
    const next = transferPointResolveStateAfter(event);
    resolveStateRef.current = next;
    setResolveState(next);
    return next;
  };

  const {
    data: connectors,
    isSuccess: capabilitiesLoaded,
    isFetching: capabilitiesFetching,
    isError: capabilitiesFailed,
  } = useConnectorCapabilities();
  const capabilitiesActionable = connectorCapabilitiesAreActionable({
    isSuccess: capabilitiesLoaded,
    isFetching: capabilitiesFetching,
  });
  const servedBy = useMemo(
    () => deriveServedByForVertical(connectors as ConnectorInfo[] | undefined, 'transfers'),
    [connectors],
  );
  /**
   * `servedBy` se sigue exigiendo para consultar el maestro (fail-closed: no
   * se lo consulta "sin filtro"), pero acá eso NO deshabilita el campo como
   * en `PlaceAutocomplete`: el resolver de direcciones no depende de los
   * conectores, y apagar el control entero dejaría el punto sin ninguna
   * forma de cargarse. Lo que falta se dice con el aviso — y para eso hace
   * falta saber POR QUÉ falta: cargando, fallada, o sin conector no son lo
   * mismo, aunque `connectorCapabilitiesAreActionable` los colapse.
   */
  const placesAuthority = transferPointPlacesAuthority({
    actionable: capabilitiesActionable,
    isFetching: capabilitiesFetching,
    isError: capabilitiesFailed,
    servedByCount: servedBy.length,
  });
  /** UNA derivación: se consulta el maestro exactamente cuando hay autoridad usable. */
  const placesReady = placesAuthority === 'ready';

  const places = usePlacesAutocomplete(debouncedQuery, {
    kinds: TRANSFER_POINT_PLACE_KINDS,
    servedBy,
    group: true,
    enabled: placesReady,
  });
  const addresses = useGeocodeAutocomplete(debouncedQuery, { sessionToken });
  const resolve = useGeocodeResolve();

  const options = useMemo(
    () =>
      buildTransferPointOptions({
        places: sortPlaceOptionsForDisplay(flattenPlaceOptions(places.data?.results ?? [])),
        predictions: addresses.data?.predictions ?? [],
      }),
    [places.data?.results, addresses.data?.predictions],
  );

  // El value controlado por MUI necesita el shape de `options`. Memoizado
  // sobre PRIMITIVOS (no la referencia): `useAutocomplete` compara el value
  // con el del render anterior por referencia para saber si la selección
  // cambió, y un objeto reconstruido inline resetea el input en cada tecla.
  const place = value?.source === 'place' ? value.place : null;
  const addressLabel = value?.source === 'address' ? value.label : '';
  const selected = useMemo<TransferPointOption | null>(
    () =>
      transferPointSelectedOption(
        place
          ? { source: 'place', place }
          : addressLabel
            ? { source: 'address', label: addressLabel }
            : null,
      ),
    [place?.id, place?.kind, place?.name, place?.code, addressLabel],
  );

  // La etiqueta del input fuera de la sesión de tipeo tiene que ser la MISMA
  // que `getOptionLabel`: si difieren, MUI repone el input por su cuenta.
  const focus = useAutocompleteFocus({
    selectedLabel: selected ? transferPointOptionLabel(selected) : '',
    onQueryChange: setQuery,
  });

  /**
   * La sesión de Google vive y muere con la sesión de TIPEO, que es lo que
   * `useAutocompleteFocus` ya define para todos los pickers del buscador:
   * elegir una opción la cierra sin devolver el foco, así que lo que se
   * teclee después empieza una sesión nueva —y pide un token nuevo— sin pasar
   * por un blur. La regla vive en `nextGeocodeSessionToken`, testeada contra
   * el reducer de foco.
   */
  /**
   * El punto se reemplazó desde afuera ("Limpiar", o invertir origen y
   * destino): el intento queda VENCIDO. Se hace durante el render y no en un
   * efecto pasivo a propósito: los efectos pasivos se difieren, así que entre
   * el click y el efecto cabe la respuesta del proveedor, que encontraría el
   * intento todavía en curso y comitearía el punto que el asesor acaba de
   * borrar. Actualizar estado en el render, comparando con lo que se vio la
   * última vez, es el patrón que React documenta para esto.
   *
   * No es la única defensa: el intento guarda la generación con la que
   * arrancó y `transferPointResolveSettled` la compara con la vigente, así
   * que una respuesta vencida no comitea NADA aunque llegue antes de este
   * render. Acá se limpia además el estado visible —el campo deja de estar
   * bloqueado esperando una respuesta que ya no importa— y un fallo anterior
   * no queda colgado sobre el punto nuevo.
   */
  const resetKeyRef = useRef(resetKey);
  if (resetKeyRef.current !== resetKey) {
    resetKeyRef.current = resetKey;
    if (resolveStateRef.current.status !== 'idle') {
      resolveStateRef.current = TRANSFER_POINT_RESOLVE_IDLE;
      setResolveState(TRANSFER_POINT_RESOLVE_IDLE);
    }
  }

  useEffect(() => {
    setSessionToken((current) => nextGeocodeSessionToken({
      editing: focus.editing,
      current,
      createToken: defaultAttemptIdFactory,
    }));
  }, [focus.editing]);

  const queryLength = debouncedQuery.trim().length;
  const placesReason = placesNoticeReason({
    ready: placesReady,
    queryLength,
    minLength: PLACES_AUTOCOMPLETE_LIMITS.queryMinLength,
    isError: places.isError,
  });
  const addressesReason = geocodeNoticeReason({
    queryLength,
    isError: addresses.isError,
    providerReason:
      addresses.data?.provider.called === false ? addresses.data.provider.reason : undefined,
  });
  const notice = transferPointNotice({
    resolving,
    resolveFailed,
    placesAuthority,
    placesReason,
    addressesReason,
    hasOptions: options.length > 0,
  });
  /**
   * Un mensaje por estado, sin `default`: si mañana aparece un estado nuevo,
   * TypeScript marca el camino sin `return` en vez de tragárselo en silencio.
   */
  const noticeMessage = ((): string | undefined => {
    switch (notice) {
    case 'resolving':
      return t('travelServices.transfers.geocode.resolving');
    case 'resolveError':
      return t('travelServices.transfers.geocode.resolveError');
    case 'noSources':
      return t('travelServices.transfers.pointNotice.noSources');
    case 'placesDisabled':
      return t('travelServices.transfers.pointNotice.placesDisabled');
    case 'placesError':
      return (
        apiErrorMessage(places.error) ??
          t('travelServices.transfers.pointNotice.placesError')
      );
    case 'addressesNotEnabled':
      return t('travelServices.transfers.pointNotice.addressesNotEnabled');
    case 'addressesBudgetExceeded':
      return t('travelServices.transfers.pointNotice.addressesBudgetExceeded');
    case 'addressesError':
      return t('travelServices.transfers.pointNotice.addressesError');
    case 'tooShort':
      return t('travelServices.places.typeToSearch', {
        count: PLACES_AUTOCOMPLETE_LIMITS.queryMinLength,
      });
    case 'empty':
      return t('travelServices.places.noResults');
    case null:
      return undefined;
    }
  })();
  /**
   * La salida a carga manual: mismas razones que antes de unificar el campo
   * (cupo agotado, geocode apagado o caído, o un resolve fallido) — el asesor
   * no puede arreglar ninguna escribiendo distinto. Cubre también el caso de
   * las DOS fuentes caídas: ahí es el único camino que queda para cargar el
   * punto.
   */
  const offerManualFallback =
    geocodeShouldOfferManualFallback(addressesReason) ||
    geocodeResolveErrorOffersManualFallback(resolveFailed);

  // Aislado (#2226): el spinner de ESTE campo se enciende solo mientras el
  // asesor tipea acá, y el resolve siempre (es una acción suya).
  const showLoading =
    (focus.editing &&
      (places.isFetching || addresses.isFetching) &&
      queryLength >= Math.min(PLACES_AUTOCOMPLETE_LIMITS.queryMinLength, GEOCODE_QUERY_MIN_LENGTH)) ||
    resolving;

  // El contenedor bloquea el swap mientras esto sea `true` (ver el prop).
  const onResolvingChangeRef = useRef(onResolvingChange);
  onResolvingChangeRef.current = onResolvingChange;
  useEffect(() => {
    onResolvingChangeRef.current?.(resolving);
  }, [resolving]);
  // Al DESMONTARSE hay que avisar que ya no se está resolviendo: este
  // componente desaparece con el resolve en vuelo cada vez que el asesor
  // pide la carga manual, limpia el formulario o el setting de places cambia.
  // Sin esto el contenedor se queda con `resolving: true` para siempre y el
  // botón de invertir origen/destino no vuelve nunca.
  useEffect(() => () => onResolvingChangeRef.current?.(false), []);

  const resolveAddress = (option: TransferPointAddressOption) => {
    // La opción sintética del valor ya elegido no tiene `placeId` y nunca
    // está en la lista: no hay nada que resolver.
    if (!option.placeId || !sessionToken) return;
    const activeSessionToken = sessionToken;
    applyResolveEvent({
      type: 'resolveStarted',
      placeId: option.placeId,
      resetKey: resetKeyRef.current,
    });
    resolve.mutate(
      { placeId: option.placeId, sessionToken: activeSessionToken },
      {
        onSuccess: (point) => {
          // Si el punto se comitea o queda fallado lo decide la máquina de
          // estados, no una segunda comparación acá: el `resolve` que devuelve
          // otro lugar mezclaría la dirección de una identidad con las
          // coordenadas de otra, y el traslado se reserva contra las
          // coordenadas.
          // El estado nuevo y "se comitea o no" salen de la MISMA decisión,
          // contra la generación de puntos VIGENTE: una respuesta vencida
          // también deja el estado en `idle`, así que deducir el commit de
          // eso repoblaría un punto ya descartado.
          const outcome = transferPointResolveSettled(
            resolveStateRef.current,
            { type: 'succeeded', placeId: point.placeId },
            resetKeyRef.current,
          );
          resolveStateRef.current = outcome.state;
          setResolveState(outcome.state);
          if (outcome.commit) {
            const { name, address } = splitGeocodeDescription(option.description);
            onChange({
              source: 'address',
              point: { name, address, lat: String(point.lat), lng: String(point.lng) },
            });
          }
        },
        onError: () => {
          const outcome = transferPointResolveSettled(
            resolveStateRef.current,
            { type: 'failed' },
            resetKeyRef.current,
          );
          resolveStateRef.current = outcome.state;
          setResolveState(outcome.state);
        },
      },
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={0.5} width="100%" maxWidth={width}>
      <Autocomplete<TransferPointOption, false, false, false>
        size="small"
        sx={{ width: '100%' }}
        options={options}
        loading={showLoading}
        // Mientras resuelve, el punto no es válido para buscar: el input se
        // deshabilita para que no se pueda elegir una segunda predicción
        // encima de un resolve en vuelo. Sale del estado del INTENTO, no de
        // `resolve.isLoading`: un intento vencido por "Limpiar" deja el campo
        // usable enseguida, aunque su request siga viva en la red.
        disabled={resolving}
        value={selected}
        onChange={(_event, option, reason) => {
          if (!focus.commitValueChange(reason)) return;
          if (!option) {
            // Vaciar el campo también cierra el intento anterior: sin esto,
            // el aviso de "no se pudo resolver" quedaría colgado sobre un
            // campo sin nada elegido.
            applyResolveEvent({ type: 'selectionCommitted' });
            onChange(null);
            return;
          }
          if (option.source === 'place') {
            // Un lugar del maestro no pasa por el resolver: cierra el intento
            // anterior, así un fallo viejo deja de presentar como fallado un
            // punto que es válido.
            applyResolveEvent({ type: 'selectionCommitted' });
            onChange({ source: 'place', place: toPlaceRef(option.place) });
            return;
          }
          resolveAddress(option);
        }}
        inputValue={focus.inputValue}
        onFocus={focus.onFocus}
        onBlur={focus.onBlur}
        onInputChange={focus.onInputChange}
        getOptionLabel={transferPointOptionLabel}
        isOptionEqualToValue={(option, sel) => option.id === sel.id}
        // El ranking lo hace cada fuente: no re-filtrar por el label.
        filterOptions={(opts) => opts}
        PopperComponent={PlaceAutocompletePopper}
        ListboxProps={{
          sx: {
            py: 0,
            '& .MuiAutocomplete-option': {
              minHeight: 64,
              alignItems: 'stretch',
              px: 2,
              py: 1,
            },
          },
        }}
        groupBy={(option) => t(transferPointOptionGroupLabelKey(option))}
        renderGroup={(params) => (
          <Box component="li" key={params.key}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                px: 2,
                pt: 1,
                pb: 0.5,
                fontWeight: 700,
                letterSpacing: 0,
                bgcolor: 'background.default',
              }}
            >
              {params.group}
            </Typography>
            <Box component="ul" sx={{ p: 0, m: 0 }}>
              {params.children}
            </Box>
          </Box>
        )}
        renderOption={(props, option) =>
          option.source === 'place' ? (
            <Box
              component="li"
              {...props}
              key={option.id}
              // `nested`, no `parent`: el contrato trae `parent` en TODO lo
              // que no sea un país (ver `PlaceOption` en `../places.ts`).
              sx={option.place.nested ? { pl: 5 } : undefined}
            >
              <PlaceOptionRow option={option.place} />
            </Box>
          ) : (
            <Box component="li" {...props} key={option.id}>
              <Box
                display="grid"
                gridTemplateColumns="24px minmax(0, 1fr)"
                alignItems="center"
                columnGap={1.5}
                width="100%"
              >
                <PlaceIcon fontSize="small" color="action" />
                <Typography variant="body2" title={option.description}>
                  {option.description}
                </Typography>
              </Box>
            </Box>
          )
        }
        noOptionsText={noticeMessage ?? t('travelServices.places.noResults')}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={notice === 'noSources' || notice === 'resolveError'}
            // Visible sin abrir el desplegable: lo que describe el estado del
            // servicio no puede depender de que el asesor abra la lista.
            helperText={
              transferPointNoticeIsPersistent(notice) ? noticeMessage : undefined
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {showLoading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {offerManualFallback && (
        <Button size="small" onClick={onUseManualFallback} sx={{ alignSelf: 'flex-start' }}>
          {t('travelServices.transfers.geocode.useManualFallback')}
        </Button>
      )}
    </Box>
  );
}
